import { authenticatedFetch } from "@lumi/data";
import { useCallback, useMemo, type ReactElement } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../auth/AuthContext";
import { createRefreshCallback } from "../../../auth/refreshCallback";
import { ContentEditor } from "../../content-editing/ContentEditor";
import { createSubmissionAdapter } from "../../content-editing/adapters/submissionAdapter";
import { useSubmissionMutations } from "../../content-editing/hooks/useSubmissionMutations";
import type { Submission } from "../../content-editing/hooks/useSubmissions";
import { useSubmission } from "../../content-editing/hooks/useSubmissions";

/**
 * Edit Existing Submission Page - Thin Wrapper
 *
 * This component wraps ContentEditor with submission-specific logic using the adapter pattern.
 * All form logic, auto-save, and UI are handled by ContentEditor.
 */
export function EditSubmission(): ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { accessToken, refreshAuth } = useAuth();

  // Fetch submission data (initial load for form)
  const { loading, error } = useSubmission(id || "");

  // Get mutation functions
  const { updateSubmission, submitForReview, resubmit } = useSubmissionMutations();

  // Create refetch function that actually fetches fresh data
  const refetchSubmission = useCallback(async (): Promise<{ data: Submission | null }> => {
    if (!accessToken || !id) {
      return { data: null };
    }

    const url = `${import.meta.env.VITE_API_BASE_URL || "http://localhost:3333"}/v1/provider/submissions/${id}`;

    try {
      const result = await authenticatedFetch<{ submission: Submission }>(
        url,
        { method: "GET" },
        accessToken,
        createRefreshCallback(refreshAuth),
      );

      return { data: result.submission };
    } catch (err) {
      console.error("Error refetching submission:", err);
      return { data: null };
    }
  }, [accessToken, refreshAuth, id]);

  // Create adapter that connects ContentEditor to submission logic
  const adapter = useMemo(
    () =>
      createSubmissionAdapter(
        id || "",
        loading,
        error?.message || null,
        refetchSubmission,
        updateSubmission,
        submitForReview,
        resubmit,
      ),
    [id, loading, error, refetchSubmission, updateSubmission, submitForReview, resubmit],
  );

  // Handle navigation back to submissions list
  const handleCancel = () => {
    navigate("/provider");
  };

  // Validate ID
  if (!id) {
    return (
      <div className="mx-auto py-12 text-center">
        <p className="text-red-400">No submission ID provided</p>
      </div>
    );
  }

  // Render ContentEditor with submission adapter
  return (
    <ContentEditor
      id={id}
      adapter={adapter}
      onCancel={handleCancel}
      breadcrumbPath={[
        { to: "/provider", label: "My Submissions" },
        { to: "", label: "Edit Submission" },
      ]}
    />
  );
}
